
/**
 * Module dependencies.
 */

import { color, media, units } from 'src/styles/utils';
import { resendConfirmAccount } from 'src/api/auth/resend-confirm-account';
import { useMutation } from 'react-query';
import { useSnackbar } from 'src/context/snackbar/context';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import React, { ReactElement, useCallback } from 'react';
import Type from 'src/components/core/typography';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  token: string
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: #dcc2b9;
  padding: ${units(3)} ${units(2)} ${units(3)} ${units(3)};

  ${media.min('md')`
    display: grid;
    grid-gap: ${units(2)};
    grid-template-columns: 1fr max-content;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Type.Paragraph)`
  color: ${color('grey400')};

  ${media.max('md')`
    margin-bottom: ${units(3)};
  `}
`;

/**
 * `ConfirmAccount` component.
 */

const ConfirmAccount = ({ token }: Props): ReactElement => {
  const { showMessage } = useSnackbar();
  const { translate } = useTranslate();
  const { isLoading, mutate } = useMutation(resendConfirmAccount);
  const handleClick = useCallback(() => {
    mutate({ token }, {
      onSuccess: () => {
        showMessage(
          translate('my-revigres:dashboard.confirmAccount.successMessage'), {
            appearance: 'default'
          }
        );
      }
    });
  }, [mutate, showMessage, token, translate]);

  return (
    <Wrapper>
      <div>
        <Type.H6>
          {translate('my-revigres:dashboard.confirmAccount.title')}
        </Type.H6>

        <Description>
          {translate('my-revigres:dashboard.confirmAccount.description')}
        </Description>
      </div>

      <Button
        disabled={!token || isLoading}
        isLoading={isLoading}
        onClick={handleClick}
        variant={'outlined'}
      >
        {translate('my-revigres:actions.resend')}
      </Button>
    </Wrapper>
  );
};

/**
 * Export `ConfirmAccount` component.
 */

export default ConfirmAccount;
